<template>
  <section class="color-picker" aria-labelledby="colors-heading">
    <div v-if="isLayered" class="color-picker__layers" role="group" aria-label="Felt layer colors">
      <button v-for="layer in ['top', 'bottom']" :key="layer" type="button" :aria-pressed="gridStore.activeLayer === layer" :class="{ active: gridStore.activeLayer === layer }" @click="gridStore.activeLayer = layer as 'top' | 'bottom'">
        <span class="color-picker__layer-heading">
          <span class="color-picker__layer-swatch" :style="getSwatchStyle(layer === 'top' ? gridStore.selectedColor || {} : bottomColor)" />
          <strong>{{ layer === 'top' ? 'TOP COLOR:' : 'BOTTOM COLOR:' }}</strong>
        </span>
        <span>{{ layer === 'top' ? gridStore.selectedColor?.label : bottomColor.label }}</span>
      </button>
    </div>
    <div class="color-picker__row color-picker__row--standard">
      <header v-if="!isLayered" class="color-picker__header">
        <h2 id="colors-heading" class="color-picker__title">{{ isWoodSlatSelection ? 'FINISHES:' : 'COLORS:' }}</h2>
        <span class="color-picker__selected-name">{{ displayColorName }}</span>
      </header>
      <div class="color-picker__grid-wrapper" :class="{ 'can-scroll-left': canScrollLeft, 'can-scroll-right': canScrollRight }">
        <button
          v-show="canScrollLeft"
          type="button"
          class="color-picker__scroll-btn color-picker__scroll-btn--left"
          aria-label="Scroll colors left"
          @click="scrollColors('left')"
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true">
            <path d="M7 1L1 7L7 13" stroke="#2f365d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div
          ref="standardGridRef"
          class="color-picker__grid color-picker__grid--standard"
          @scroll="updateScrollState"
        >
          <button
            v-for="(color, index) in colors"
            :key="`${index}-${color.label}`"
            :disabled="gridStore.isProcessing"
            type="button"
            :class="{ active: isSameColor(activeColorLabel, color.label) }"
            :style="getSwatchStyle(color)"
            :aria-pressed="isSameColor(activeColorLabel, color.label)"
            :aria-label="color.label"
            :aria-describedby="`color-tooltip-${index}`"
            @click="picker.selectColor(color, $event)"
            @mouseenter="hoveredColor = color.label"
            @mouseleave="hoveredColor = null"
          >
            <span :id="`color-tooltip-${index}`" class="color-picker__tooltip" role="tooltip">{{ color.label }}</span>
          </button>
        </div>
        <button
          v-show="canScrollRight"
          type="button"
          class="color-picker__scroll-btn color-picker__scroll-btn--right"
          aria-label="Scroll colors right"
          @click="scrollColors('right')"
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true">
            <path d="M1 1L7 7L1 13" stroke="#2f365d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
    <div
      v-if="showWoodGrain"
      class="color-picker__row color-picker__row--wood"
    >
      <header class="color-picker__header color-picker__header--wood">
        <span class="color-picker__title">WOOD GRAIN PATTERNS: <span class="color-picker__selected-name">{{ displayWoodLabel }}</span></span>
        <span class="color-picker__subtitle">*Only Available on 3D Blocks - Blank</span>
      </header>
      <div class="color-picker__grid color-picker__grid--wood">
        <button
          v-for="swatch in woodSwatches"
          :key="swatch.label"
          type="button"
          class="color-picker__swatch color-picker__swatch--wood"
          :class="{ active: gridStore.selectedColor?.label === swatch.label }"
          :style="{ backgroundImage: `url(${swatch.image})`, backgroundSize: 'cover' }"
          :aria-pressed="gridStore.selectedColor?.label === swatch.label"
          :aria-label="swatch.label"
          :title="swatch.label"
          @click="picker.selectColor(swatch, $event)"
          @mouseenter="hoveredWood = swatch.label"
          @mouseleave="hoveredWood = null"
        />
      </div>
    </div>
    <button v-if="!isLayered" class="color-picker__select" type="button" :disabled="!gridStore.selectedColor" @click="selectCurrentColor">
      Select all {{ gridStore.selectedColor?.label || 'color' }} tiles
    </button>
  </section>
</template>

<script setup lang="ts">
import { isLayeredShape, isSameColor, getFeltColorHex } from '~/shared/layeredEngraving'
import feltColors from '~/server/utils/threeDDesignSync/felt-colors.json'
import { GRID_PATTERN_IDS } from '~/composables/grid/constants'
import { WOOD_GRAIN_LABELS, isWoodGrainColor } from '~/composables/shapes/colors'
import { GRID_SHAPES } from '~/composables/shapes/constants'
import { getColorsForPattern } from '~/composables/shapes/printedPatterns'
import { isBlank3DBox } from '~/composables/shapes/shapesFor3D'
import { getColorVariation } from '~/composables/shapes/utils'
import { loadVariations } from '~/composables/shapes/utils'
import { getWoodSlatFinishChoices, isWoodSlatShapeId } from '~/composables/shapes/woodSlats'
import type { Shape } from '~/types/grid.types'
import { createPickerInteractions, filterStudioColors } from './logic'

const gridStore = useGridStore()
const userStore = useUserStore()
const actions = useNewStudioActions()
const { fetchConfig, getColorFlags } = useStudioConfig()
const { getColors } = useStudioColors()
const { getSwatchStyle } = useColorSwatch()
const interactions = useShapeInteractions()
const picker = createPickerInteractions(interactions)
const isLayered = computed(() => isLayeredShape(gridStore.selectedShape))
const bottomColor = computed(() => {
  if (gridStore.layeredBottomColor) return gridStore.layeredBottomColor
  const config = gridStore.selectedShape && gridStore.selectedColor
    ? getColorVariation(gridStore.selectedShape, gridStore.selectedColor, { allowFallback: false })?.layered_engraving
    : undefined
  const label = config?.default_bottom_color || 'Ivory'
  const match = getColors().find(c => isSameColor(c.label, label))
  if (match) return { ...match, hex: match.hex || getFeltColorHex(match.label) || feltColors.Ivory }
  return { label, hex: config?.default_bottom_hex || getFeltColorHex(label) || feltColors.Ivory, image: '' }
})
const activeColorLabel = computed(() => isLayered.value && gridStore.activeLayer === 'bottom' ? bottomColor.value.label : gridStore.selectedColor?.label)
const isWoodSlatSelection = computed(() => isWoodSlatShapeId(gridStore.selectedShape?.id))
const hasVariationForGrid = (colorLabel: string) => {
  if (isLayered.value) {
    if (gridStore.activeLayer === 'bottom') return Boolean(getColors().find(c => isSameColor(c.label, colorLabel))?.hex || getFeltColorHex(colorLabel))
    return Boolean(getColorVariation(gridStore.selectedShape!, { label: colorLabel, image: '' }, { allowFallback: false }))
  }
  if (isWoodSlatSelection.value && gridStore.selectedShape) {
    return Boolean(getColorVariation(gridStore.selectedShape, { label: colorLabel, image: '' }, { allowFallback: false }))
  }
  const patternId = gridStore.gridPattern?.id as keyof typeof GRID_SHAPES | undefined
  const shapes = patternId ? GRID_SHAPES[patternId] : undefined
  if (!shapes?.length) return true
  const normalize = (value: string) => value.replace(/\s+/g, '').toLowerCase()
  return shapes.some((shape: Shape) => {
    const variation = getColorVariation(shape, { label: colorLabel, image: '' }, { allowFallback: false })
    return variation && normalize(variation.label).includes(normalize(colorLabel))
  })
}
const is3DGrid = computed(() => gridStore.gridPattern?.id === GRID_PATTERN_IDS.THREE_D)

const woodSwatches = computed(() => filterStudioColors({
  colors: getColors().filter(c => isWoodGrainColor(c.label)),
  getColorFlags,
  isAdmin: userStore.isAdmin,
  patternAllowlist: null,
  hasVariation: () => true,
}))

// Wood grain is only available on blank 3D boxes (not engraved variants).
const is3DBlankBox = computed(() => isBlank3DBox(gridStore.selectedShape?.prefix))

const showWoodGrain = computed(() => is3DGrid.value && is3DBlankBox.value && woodSwatches.value.length > 0)

// Reactive trigger that flips when variations finish loading, so the colors
// computed re-evaluates and can apply the pattern allowlist filter.
const variationsReady = ref(false)
onMounted(async () => {
  // Eagerly load variations so the color picker can filter by available
  // pattern colors (e.g. only show the 4 colors available for each printed
  // pattern). We use a local direct fetch here because the studio colors
  // state may not be available in the mobile shell's app instance.
  try {
    await loadVariations()
    variationsReady.value = true
  } catch (e) {
    // ignore — fallback to all colors if variations can't load
  }
  // Ensure studio colors are fetched too
  try {
    await fetchColors()
  } catch (e) {
    // ignore
  }
})

const colors = computed(() => {
  // Touch variationsReady so this computed re-evaluates when variations load
  // (the actual value is unused, we just need the reactive dependency)
  if (variationsReady.value) { /* ready */ }
  const baseColors = isWoodSlatSelection.value
    ? getWoodSlatFinishChoices(gridStore.selectedShape?.id)
    : getColors().filter(c => !WOOD_GRAIN_LABELS.includes(c.label) && !c.label.includes(' / '))

  const filtered = filterStudioColors({
    colors: baseColors,
    getColorFlags,
    isAdmin: userStore.isAdmin,
    patternAllowlist: gridStore.selectedShape?.allowedColors || getColorsForPattern(gridStore.selectedShape?.prefix, getColors()),
    hasVariation: hasVariationForGrid,
  })

  // Safe fallback: never let the color palette disappear into an empty list
  if (!isLayered.value && filtered.length === 0 && baseColors.length > 0 && !gridStore.selectedShape?.allowedColors) {
    // If we have a pattern allowlist, still filter by it (for printed patterns
    // and wood slats) even if the studio config returned no admin flags. This
    // ensures the mobile picker shows only the 4 available pattern colors.
    const allowlist = getColorsForPattern(gridStore.selectedShape?.prefix, baseColors)
    const fallback = allowlist && allowlist.length > 0
      ? baseColors.filter(c => allowlist.includes(c.label))
      : baseColors.filter(c => {
        const flags = getColorFlags(c.label, c.admin, c.active)
        return flags.active && (!flags.admin || userStore.isAdmin)
      })
    return fallback
  }

  return filtered
})

const hoveredColor = ref<string | null>(null)
const selectedFamily = computed(() => gridStore.selectedColor?.label || '')
const displayColorName = computed(() => hoveredColor.value || selectedFamily.value)

const hoveredWood = ref<string | null>(null)
const selectedWoodLabel = computed(() => {
  const match = woodSwatches.value.find(s => s.label === gridStore.selectedColor?.label)
  if (match) return match.label
  return woodSwatches.value[0]?.label || ''
})
const displayWoodLabel = computed(() => hoveredWood.value || selectedWoodLabel.value)
const selectCurrentColor = actions.selectCurrentColor

const standardGridRef = ref<HTMLElement | null>(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
let resizeObserver: ResizeObserver | null = null
let checkTimer: ReturnType<typeof setInterval> | null = null

const updateScrollState = () => {
  const el = standardGridRef.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 6
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 6
}

const scrollColors = (direction: 'left' | 'right') => {
  const el = standardGridRef.value
  if (!el) return
  const step = Math.max(160, Math.floor(el.clientWidth * 0.65))
  const delta = direction === 'left' ? -step : step
  const target = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + delta))

  el.scrollTo({ left: target, behavior: 'smooth' })
  setTimeout(() => {
    if (Math.abs(el.scrollLeft - target) > 10) {
      el.scrollTo({ left: target, behavior: 'instant' })
    }
    updateScrollState()
  }, 250)
}

const onGridWheel = (e: WheelEvent) => {
  const el = standardGridRef.value
  if (!el) return
  // Convert standard vertical mouse wheel into horizontal scroll when content overflows
  if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) {
    e.preventDefault()
    const target = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + e.deltaY))
    el.scrollTo({ left: target, behavior: 'instant' })
    updateScrollState()
  }
}

watch(colors, () => {
  nextTick(() => {
    updateScrollState()
    setTimeout(updateScrollState, 100)
  })
})

onMounted(() => {
  fetchConfig()
  const el = standardGridRef.value
  if (el) {
    el.addEventListener('wheel', onGridWheel, { passive: false })
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateScrollState())
      resizeObserver.observe(el)
    }
  }
  checkTimer = setInterval(() => {
    updateScrollState()
    if (canScrollRight.value) {
      if (checkTimer) clearInterval(checkTimer)
      checkTimer = null
    }
  }, 250)
  setTimeout(() => {
    if (checkTimer) clearInterval(checkTimer)
    checkTimer = null
  }, 4000)
  window.addEventListener('resize', updateScrollState)
})

onUnmounted(() => {
  if (checkTimer) clearInterval(checkTimer)
  const el = standardGridRef.value
  if (el) {
    el.removeEventListener('wheel', onGridWheel)
    resizeObserver?.disconnect()
  }
  window.removeEventListener('resize', updateScrollState)
})
</script>

<style scoped>
.color-picker {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 4px 16px 8px;
  background: var(--studio-surface);
  border-bottom: 1px solid var(--studio-shell-border);
  box-sizing: border-box;
  user-select: none;
  gap: 6px;
}

.color-picker__row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.color-picker__row--wood {
  gap: 2px;
  margin-top: 4px;
  min-width: 355px;
}

/* When space allows (e.g. 1920px display where canvas column > 1420px), place wood section directly on the side */
@container (min-width: 1420px) {
  .color-picker {
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 12px;
  }

  .color-picker__row--standard {
    width: auto;
    flex: 0 0 auto;
    min-width: 0;
  }

  .color-picker__row--wood {
    width: 350px;
    min-width: 350px;
    flex: 0 0 350px;
    align-self: center;
    margin-top: 0;
    border-left: 1px solid var(--studio-border);
    padding-left: 14px;
  }
}

.color-picker__header {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-bottom: 4px;
}

.color-picker__header--wood {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 6px;
}
.color-picker__title {
  margin: 0;
  color: #a3a39e;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
  font-family: inherit;
}

.color-picker__header--wood .color-picker__title {
  color: #5f5e59;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 1.04px;
  white-space: nowrap;
}

.color-picker__subtitle {
  margin: 0;
  color: #444440;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.4;
  font-family: inherit;
}

.color-picker__selected-name {
  color: #2f365d;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
}

.color-picker__header--wood .color-picker__selected-name {
  color: #313558;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
}

.color-picker__grid-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
}

.color-picker__scroll-btn {
  position: absolute;
  z-index: 30;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #d3d2ce;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.color-picker__scroll-btn:hover {
  background: #ffffff;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
}

.color-picker__scroll-btn--left {
  left: 2px;
}

.color-picker__scroll-btn--right {
  right: 2px;
}

/* Subtle fade indicators on the edges when content overflows */
.color-picker__grid-wrapper::before,
.color-picker__grid-wrapper::after {
  content: '';
  position: absolute;
  top: 4px;
  bottom: 6px;
  width: 28px;
  pointer-events: none;
  z-index: 5;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.color-picker__grid-wrapper::before {
  left: 0;
  background: linear-gradient(to right, rgba(255, 255, 255, 0.95), transparent);
}

.color-picker__grid-wrapper::after {
  right: 0;
  background: linear-gradient(to left, rgba(255, 255, 255, 0.95), transparent);
}

.color-picker__grid-wrapper.can-scroll-left::before {
  opacity: 1;
}

.color-picker__grid-wrapper.can-scroll-right::after {
  opacity: 1;
}

.color-picker__grid {
  display: grid;
  grid-template-rows: repeat(2, 37.24px);
  grid-auto-flow: column;
  grid-auto-columns: 37.24px;
  gap: 7px clamp(9.8px, 1.1vw, 16px);
  justify-content: flex-start;
  overflow-x: auto;
  scroll-behavior: smooth;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  max-width: 100%;
  padding: 4px 4px 6px;
  scrollbar-width: thin;
}

.color-picker__grid--wood {
  grid-template-rows: 37.24px;
  grid-auto-flow: column;
  grid-auto-columns: 37.24px;
  gap: 7px 9.8px;
  padding: 4px 4px 6px;
}

.color-picker__grid button {
  position: relative;
  width: 37.24px;
  height: 37.24px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 50%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 125%;
  cursor: pointer;
  flex-shrink: 0;
}

.color-picker__swatch--wood {
  background-color: #fff;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.color-picker__grid--wood button {
  width: 37.24px;
  height: 37.24px;
  border: 0;
  border-radius: 50%;
  background-clip: padding-box;
}

.color-picker__swatch--wood.active {
  border-color: transparent;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #2f7fec;
}

.color-picker__tooltip {
  position: absolute;
  z-index: 50;
  top: calc(100% + 8px);
  left: 50%;
  min-width: 2px;
  padding: 6px 8px;
  transform: translateX(-50%);
  border-radius: 4px;
  background: #8196a9;
  color: #fff;
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgb(0 0 0 / 16%);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.color-picker__tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-bottom-color: #8196a9;
}

/* Bottom row swatches in the 2-row grid (even children): pop UP so they never trigger vertical scroll or clipping */
.color-picker__grid--standard button:nth-child(2n) .color-picker__tooltip {
  top: auto;
  bottom: calc(100% + 7px);
}

.color-picker__grid--standard button:nth-child(2n) .color-picker__tooltip::after {
  top: 100%;
  bottom: auto;
  border-bottom-color: transparent;
  border-top-color: #8196a9;
}

.color-picker__grid button:hover .color-picker__tooltip,
.color-picker__grid button:focus-visible .color-picker__tooltip {
  opacity: 1;
  visibility: visible;
}

.color-picker__grid button.active {
  border-color: transparent;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #2f7fec;
}

.color-picker__select {
  display: none;
}
</style>
<style scoped>
.color-picker:has(> .color-picker__layers) { padding-block: 6px; }
.color-picker__layers { display: flex; gap: 12px; padding: 7px 0; }
@container (min-width: 1420px) { .color-picker__layers { align-self: center; } }
.color-picker__layers button { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 6px; min-height: 44px; min-width: 0; padding: 5px 12px; border: 2px solid var(--studio-border); border-radius: 0; background: var(--studio-surface); color: var(--studio-text); font: inherit; font-size: 12px; }
.color-picker__layers button.active { outline: 2px solid var(--studio-focus-ring, #80aafa); outline-offset: 2px; }
.color-picker__layer-heading { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.color-picker__layer-swatch { flex-shrink: 0; display: inline-block; width: 25px; height: 25px; border-radius: 50%; background-size: cover; }
@media (max-width: 700px) { .color-picker__layers { gap: 7px; } .color-picker__layers button { flex: 1; padding: 4px; font-size: 10px; } }
</style>
